import React, { useMemo, useState } from 'react';
import { Button, IconButton, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { AddEffectRegistrationEvaluationTimeOptionRequest } from 'requests/nswag/nllMedicineTasks/NllMedicineTasksClient';
import LoadingScreen from 'Components/LoadingScreen';
import { useCustomerContext } from 'requests/hooks/useCustomerContext';
import useAddEvaluationTimeOption from 'requests/hooks/useAddEvaluationTimeOption';
import useDeleteEvaluationTimeOption from 'requests/hooks/useDeleteEvaluationTimeOption';
import useFetchEvaluationTimeOffsets from 'requests/hooks/useFetchEvaluationTimeOffsets';
import AddEffectRegistrationTimeOptionDialog from './AddEffectRegistrationTimeOptionDialog';
import DeleteEffectRegistrationTimeOptionDialog from './DeleteEffectRegistrationTimeOptionDialog';
import { formatOffset } from './formatOffset';

const EffectRegistrationEvaluationTimeOptionsView: React.FC = () => {
  const customer = useCustomerContext();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError } = useFetchEvaluationTimeOffsets(customer.id);
  const addMutation = useAddEvaluationTimeOption();
  const deleteMutation = useDeleteEvaluationTimeOption();
  const [addOpen, setAddOpen] = useState(false);
  const [minutesToDelete, setMinutesToDelete] = useState<number | undefined>(undefined);

  const offsets = useMemo(() => [...(data ?? [])].sort((a, b) => a - b), [data]);

  const [isSaving, setIsSaving] = useState(false);

  const onAdd = async (minutes: number, shouldBeActiveOnModifiedChainConfigs: boolean) => {
    setIsSaving(true);
    try {
      await addMutation.mutateAsync({
        customerId: customer.id,
        request: new AddEffectRegistrationEvaluationTimeOptionRequest({
          minutes,
          shouldBeActiveOnModifiedChainConfigs,
        }),
      });
      enqueueSnackbar(`Added ${formatOffset(minutes)}`, { variant: 'success' });
      setAddOpen(false);
    } catch {
      enqueueSnackbar(`Could not add ${formatOffset(minutes)}`, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async (minutes: number, alsoRemoveIfCreatedOnChainConfig: boolean) => {
    setIsSaving(true);
    try {
      await deleteMutation.mutateAsync({
        customerId: customer.id,
        minutes,
        alsoRemoveIfCreatedOnChainConfig,
      });
      enqueueSnackbar(`Deleted ${formatOffset(minutes)}`, { variant: 'success' });
      setMinutesToDelete(undefined);
    } catch {
      enqueueSnackbar(`Could not delete ${formatOffset(minutes)}`, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <Stack sx={{ marginTop: '16px', padding: '16px', width: 'fit-content', minWidth: 320, justifySelf: 'center' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={4}>
        <Typography variant="h5">Effect registration evaluation time options</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} disabled={isError}>
          Add option
        </Button>
      </Stack>
      {isError && (
        <Typography color="error" sx={{ mt: 2 }}>
          Could not load evaluation time options
        </Typography>
      )}
      {!isError && offsets.length === 0 && <Typography sx={{ mt: 2 }}>No options configured</Typography>}
      <List>
        {offsets.map((minutes) => (
          <ListItem
            divider
            key={minutes}
            secondaryAction={
              <IconButton
                edge="end"
                aria-label={`Delete ${minutes} minutes`}
                onClick={() => setMinutesToDelete(minutes)}
              >
                <DeleteIcon />
              </IconButton>
            }
          >
            <ListItemText primary={formatOffset(minutes)} />
          </ListItem>
        ))}
      </List>
      <AddEffectRegistrationTimeOptionDialog
        open={addOpen}
        existingOffsets={offsets}
        isSaving={isSaving}
        onClose={() => setAddOpen(false)}
        onAdd={onAdd}
      />
      <DeleteEffectRegistrationTimeOptionDialog
        minutes={minutesToDelete}
        isDeleting={isSaving}
        onClose={() => setMinutesToDelete(undefined)}
        onDelete={onDelete}
      />
    </Stack>
  );
};

export default EffectRegistrationEvaluationTimeOptionsView;
